import type { Preventivo, EstimateLevel } from '../../types';
import { formatCurrency, getPrice, getEstimateLevelLabel } from '../../utils/pricing';

interface PreventivoCardProps {
  preventivo: Preventivo;
  estimateLevel: EstimateLevel;
  onEdit?: () => void;
  onDelete?: () => void;
  isSelected?: boolean;
  onClick?: () => void;
}

export function PreventivoCard({
  preventivo,
  estimateLevel,
  onEdit,
  onDelete,
  isSelected,
  onClick,
}: PreventivoCardProps) {
  const currentPrice = getPrice(preventivo, estimateLevel);

  return (
    <div
      className={`
        card-hover cursor-pointer transition-all
        ${isSelected ? 'ring-2 ring-primary-500 bg-primary-50' : 'bg-white'}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 mb-2">
            {preventivo.categoria}
          </span>
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {preventivo.voce}
          </h3>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-bold text-primary-600 currency">
              {formatCurrency(currentPrice)}
            </span>
            <span className="text-xs text-gray-500">
              ({getEstimateLevelLabel(estimateLevel)})
            </span>
          </div>
        </div>

        {(onEdit || onDelete) && (
          <div className="flex items-center gap-1 ml-2">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Modifica"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                title="Elimina"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Price range preview */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex justify-between text-xs text-gray-500">
          <span>Min: {formatCurrency(parseFloat(preventivo.costoMin))}</span>
          <span>Med: {formatCurrency(parseFloat(preventivo.costoMed))}</span>
          <span>Max: {formatCurrency(parseFloat(preventivo.costoMax))}</span>
        </div>
      </div>

      {preventivo.note && (
        <p className="mt-2 text-xs text-gray-500 line-clamp-2">
          {preventivo.note}
        </p>
      )}
    </div>
  );
}
